/**
 * Mitarbeiter — Lokale Demo-Daten und Typen
 * Stand: 24.03.2026 (aus AGENT_BOOTSTRAP.md)
 * 
 * Rollen: Admin, Sachbearbeiter, Mitarbeiter, Kiosk
 * Berechtigungen pro Betrieb werden später über Supabase RLS gesteuert.
 */

import type { BetriebId } from './betriebe';

export type MitarbeiterRolle = 'admin' | 'sachbearbeiter' | 'mitarbeiter' | 'kiosk' | 'ki_agent';

export interface MitarbeiterPermission {
  betriebId: BetriebId;
  aktenLesen: boolean;
  aktenSchreiben: boolean;
  rechnungen: boolean;
  buchhaltung: boolean;
  einstellungen: boolean;
}

export interface MitarbeiterLocal {
  id: string;
  vorname: string;
  nachname: string;
  email: string;
  telefon: string;
  rolle: MitarbeiterRolle;
  initialen: string;
  avatarColor: string;
  businessUnits: BetriebId[];
  istAktiv: boolean;
  istOnline: boolean;
  letzteAktivitaet: string;
  /** Berechtigungen pro Betrieb — ○ geplant (UI-Gerüst) */
  berechtigungen: MitarbeiterPermission[];
}

export const ROLLEN_LABELS: Record<MitarbeiterRolle, string> = {
  admin: 'Administrator',
  sachbearbeiter: 'Sachbearbeiter',
  mitarbeiter: 'Mitarbeiter',
  kiosk: 'Kiosk-Modus',
  ki_agent: 'System',
};

export const ROLLEN_COLORS: Record<MitarbeiterRolle, string> = {
  admin: 'bg-red-100 text-red-700',
  sachbearbeiter: 'bg-blue-100 text-blue-700',
  mitarbeiter: 'bg-gray-100 text-gray-600',
  kiosk: 'bg-amber-100 text-amber-700',
  ki_agent: 'bg-purple-100 text-purple-700',
};

/** Demo-Mitarbeiter — echte Daten aus AGENT_BOOTSTRAP.md */
export const DEMO_MITARBEITER: MitarbeiterLocal[] = [
  {
    id: 'ma-001',
    vorname: 'Andreas',
    nachname: 'Koop',
    email: 'andila111@googlemail.com',
    telefon: '',
    rolle: 'admin',
    initialen: 'AK',
    avatarColor: '#B91C1C',
    businessUnits: ['gutachten', 'autoglas', 'mietwagen', 'werkstatt', 'protex', 'unfallgiganten', 'airbnb'],
    istAktiv: true,
    istOnline: true,
    letzteAktivitaet: 'Gerade aktiv',
    berechtigungen: [],
  },
  {
    id: 'ma-002',
    vorname: 'Ivan',
    nachname: 'Dalmann',
    email: 'dalmann@gutachter-koop.de',
    telefon: '',
    rolle: 'sachbearbeiter',
    initialen: 'ID',
    avatarColor: '#2563EB',
    businessUnits: ['gutachten', 'autoglas', 'werkstatt'],
    istAktiv: true,
    istOnline: true,
    letzteAktivitaet: 'Vor 5 Min.',
    berechtigungen: [],
  },
  {
    id: 'ma-003',
    vorname: 'Viktor',
    nachname: 'Malinin',
    email: '',
    telefon: '',
    rolle: 'sachbearbeiter',
    initialen: 'VM',
    avatarColor: '#059669',
    businessUnits: ['gutachten', 'werkstatt'],
    istAktiv: true,
    istOnline: false,
    letzteAktivitaet: 'Vor 2 Std.',
    berechtigungen: [],
  },
  {
    id: 'ma-004',
    vorname: 'Tanja',
    nachname: 'Remkhe',
    email: 'remkhe@gutachter-koop.de',
    telefon: '',
    rolle: 'sachbearbeiter',
    initialen: 'TR',
    avatarColor: '#7C3AED',
    businessUnits: ['gutachten', 'autoglas', 'mietwagen'],
    istAktiv: true,
    istOnline: false,
    letzteAktivitaet: 'Vor 1 Std.',
    berechtigungen: [],
  },
  {
    id: 'ma-005',
    vorname: 'Irina',
    nachname: 'Hahn',
    email: '',
    telefon: '+4916098655699',
    rolle: 'mitarbeiter',
    initialen: 'IH',
    avatarColor: '#D97706',
    businessUnits: ['gutachten'],
    istAktiv: true,
    istOnline: false,
    letzteAktivitaet: 'Heute, 09:15',
    berechtigungen: [],
  },
  {
    id: 'ma-006',
    vorname: 'Andreas',
    nachname: 'Mill',
    email: '',
    telefon: '',
    rolle: 'mitarbeiter',
    initialen: 'AM',
    avatarColor: '#0D9488',
    businessUnits: ['gutachten', 'werkstatt'],
    istAktiv: true,
    istOnline: false,
    letzteAktivitaet: 'Gestern, 17:30',
    berechtigungen: [],
  },
];

/** Hilfsfunktion: Mitarbeiter nach ID finden */
export function getMitarbeiterById(id: string): MitarbeiterLocal | undefined {
  return DEMO_MITARBEITER.find(m => m.id === id);
}

/** Hilfsfunktion: Mitarbeiter nach Betrieb filtern */
export function getMitarbeiterByBetrieb(betriebId: BetriebId): MitarbeiterLocal[] {
  return DEMO_MITARBEITER.filter(m => m.businessUnits.includes(betriebId));
}

/** Hilfsfunktion: Online-Mitarbeiter */
export function getOnlineMitarbeiter(): MitarbeiterLocal[] {
  return DEMO_MITARBEITER.filter(m => m.istOnline && m.istAktiv);
}
